import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SEOHead } from "@/components/SEOHead";
import { 
  Mail, 
  Phone, 
  Search, 
  ArrowLeft, 
  Shield, 
  CheckCircle,
  AlertCircle,
  HelpCircle
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

const ForgotPassword = () => {
  const { resetPassword, findAccountByEmail, findAccountByPhone } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [foundAccounts, setFoundAccounts] = useState<string[]>([]);

  // Email password reset
  const handleEmailReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const email = String(formData.get("email") || "").trim();

      if (!email) {
        toast.error("Please enter your email address");
        return;
      }

      console.log("Attempting password reset for:", email);

      const { error } = await resetPassword(email);

      if (error) {
        console.error("Password reset error:", error);
        toast.error(error.message || "Failed to send reset email");
        return;
      }

      setEmailSent(true);
      toast.success("Password reset email sent! Check your inbox.");

    } catch (err: any) {
      console.error("Unexpected password reset error:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Account recovery by email
  const handleEmailRecovery = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const email = String(formData.get("recovery-email") || "").trim();

      if (!email) {
        toast.error("Please enter an email address");
        return;
      }

      const { exists, error } = await findAccountByEmail(email);

      if (error) {
        toast.error(error.message || "Failed to search for account");
        return;
      }

      if (exists) {
        setFoundAccounts([email]);
        toast.success("Account found! You can request a password reset for this email.");
      } else {
        toast.error("No account found with this email address");
        setFoundAccounts([]);
      }

    } catch (err: any) {
      console.error("Account recovery error:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Account recovery by phone (placeholder for future implementation)
  const handlePhoneRecovery = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const phone = String(formData.get("recovery-phone") || "").trim();

      if (!phone) {
        toast.error("Please enter a phone number");
        return;
      }

      const { error } = await findAccountByPhone(phone);

      if (error) {
        if (error.message.includes("not yet implemented")) {
          toast.error("Phone recovery is coming soon! Please use email recovery for now.");
        } else {
          toast.error(error.message || "Failed to search for account");
        }
        return;
      }

      // This would be implemented when phone recovery is available
      toast.info("Phone recovery feature coming soon!");

    } catch (err: any) {
      console.error("Phone recovery error:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const EmailResetTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Reset Password
          </CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailSent ? (
            <div className="text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
              <div>
                <h3 className="text-lg font-semibold">Check your email</h3>
                <p className="text-muted-foreground">
                  We've sent a password reset link to your email address.
                </p>
              </div>
              <div className="space-y-2">
                <Button onClick={() => setEmailSent(false)} variant="outline" className="w-full">
                  Send to a different email
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleEmailReset} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                  className="mt-1"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:underline">
                  Remember your password? Sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          For security reasons, we'll send the reset link even if the email doesn't exist in our system.
        </AlertDescription>
      </Alert>
    </div>
  );

  const AccountRecoveryTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Find Your Account
          </CardTitle>
          <CardDescription>
            Can't remember which email you used? Search for your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="email-search" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email-search">Search by Email</TabsTrigger>
              <TabsTrigger value="phone-search">Search by Phone</TabsTrigger>
            </TabsList>

            <TabsContent value="email-search" className="space-y-4">
              <form onSubmit={handleEmailRecovery} className="space-y-4">
                <div>
                  <Label htmlFor="recovery-email">Email Address</Label>
                  <Input
                    id="recovery-email"
                    name="recovery-email"
                    type="email"
                    placeholder="Try different email addresses you might have used"
                    required
                    disabled={loading}
                    className="mt-1"
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Searching..." : "Search for Account"}
                </Button>
              </form>

              {foundAccounts.length > 0 && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Account found!</p>
                      <p>You can now request a password reset for: <strong>{foundAccounts[0]}</strong></p>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          setFoundAccounts([]);
                          // Switch to reset tab and pre-fill email
                          const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement;
                          if (emailInput) {
                            emailInput.value = foundAccounts[0];
                          }
                        }}
                        className="mt-2"
                      >
                        Reset Password for This Account
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="phone-search" className="space-y-4">
              <form onSubmit={handlePhoneRecovery} className="space-y-4">
                <div>
                  <Label htmlFor="recovery-phone">Phone Number</Label>
                  <Input
                    id="recovery-phone"
                    name="recovery-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    required
                    disabled={loading}
                    className="mt-1"
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Searching..." : "Search for Account"}
                </Button>
              </form>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Phone number recovery is coming soon! Please use email search for now.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );

  const HelpTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Still Need Help?
          </CardTitle>
          <CardDescription>
            Common issues and how to resolve them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Can't find your account?</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Try different email addresses you might have used</li>
              <li>Check if you signed up with a social provider (Google, Microsoft)</li>
              <li>Look for old confirmation emails in your inbox</li>
            </ul>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Not receiving reset emails?</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Check your spam/junk folder</li>
              <li>Make sure the email address is correct</li>
              <li>Wait a few minutes - emails can be delayed</li>
              <li>Add noreply@bahamarket.com to your contacts</li>
            </ul>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Account Security</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Reset links expire after 24 hours for security</li>
              <li>You can request multiple reset emails</li>
              <li>Only the most recent reset link will work</li>
            </ul>
          </div>

          <Separator />

          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Still having trouble accessing your account?
            </p>
            <Button variant="outline" size="sm">
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <SEOHead
        title="Reset Password - Bahama Marketplace"
        description="Reset your Bahama Marketplace account password securely. Get back to shopping from amazing local sellers."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Account Recovery</h1>
                <p className="text-muted-foreground">
                  Recover your account and reset your password.
                </p>
              </div>
            </div>

            <Tabs defaultValue="reset" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="reset">Reset Password</TabsTrigger>
                <TabsTrigger value="recovery">Find Account</TabsTrigger>
                <TabsTrigger value="help">Help</TabsTrigger>
              </TabsList>

              <TabsContent value="reset">
                <EmailResetTab />
              </TabsContent>

              <TabsContent value="recovery">
                <AccountRecoveryTab />
              </TabsContent>

              <TabsContent value="help">
                <HelpTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;